import L from 'leaflet';
import { UNASSIGNED_COLOR, UNASSIGNED_DARK } from './liveMapRules';

/**
 * Canlı kurye takibi harita işaretleri — restoran panelindeki ekranla aynı görsel dil:
 *   kurye      : üstte isim balonu + kurye renginde daire (ortada paket sayısı, köşede konum tazeliği)
 *   teslim     : kurye renginde numaralı damla + üstünde fiş balonu; paket restorandaysa içi boş + kesikli
 *   atanmamış  : camgöbeği kare + ünlem, üstünde koyu balon
 *   restoran   : koyu damla + ev
 * İşaretler SVG metni olarak üretilir; ad/numara gibi dış veri `esc` ile kaçırılır.
 */

const esc = (s) => String(s ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const FONT = "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif";
const SHADOW = '<defs><filter id="s" x="-20%" y="-40%" width="140%" height="180%"><feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-opacity="0.35"/></filter></defs>';
const BUBBLE_H = 24;
const PAUSED_COLOR = '#64748b';

/** 12px kalın sistem yazısında ortalama karakter ~7,4px; Türkçe büyük harfler için pay bırakılır. */
const bubbleWidth = (text, min, pad) => Math.max(min, Math.round(String(text).length * 7.4 + pad));

const icon = (svg, width, height, anchorX, anchorY, className) => L.divIcon({
    html: svg,
    className: `lm-ico ${className || ''}`,
    iconSize: [width, height],
    iconAnchor: [anchorX, anchorY],
});

/** Kurye: konum noktası dairenin merkezidir; isim balonu onun üstünde kalır. */
export function courierIcon({ name, color, freshColor, count, paused }) {
    const label = paused ? `Durduruldu, ${name}` : name;
    const DOT = 46;
    const GAP = 4;
    const bw = bubbleWidth(label, 56, 22);
    const width = Math.max(DOT, bw);
    const height = BUBBLE_H + GAP + DOT;
    const cx = width / 2;
    const cy = BUBBLE_H + GAP + DOT / 2;
    const stroke = paused
        ? `stroke="${PAUSED_COLOR}" stroke-width="2" stroke-dasharray="4 3"`
        : `stroke="${freshColor}" stroke-width="2"`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${SHADOW}
        <rect x="${(width - bw) / 2 + 1}" y="1" width="${bw - 2}" height="${BUBBLE_H - 2}" rx="11" fill="${paused ? '#f1f5f9' : '#ffffff'}" ${stroke} filter="url(#s)"/>
        <text x="${cx}" y="16.5" text-anchor="middle" font-size="12" font-weight="800" fill="#0f172a" font-family="${FONT}">${esc(label)}</text>
        <circle cx="${cx}" cy="${cy}" r="17" fill="${color}" stroke="#ffffff" stroke-width="3"/>
        <circle cx="${cx}" cy="${cy}" r="20" fill="none" stroke="${color}" stroke-width="2" opacity="0.35"/>
        ${count > 0 ? `<text x="${cx}" y="${cy + 4.5}" text-anchor="middle" font-size="13" font-weight="800" fill="#fff" font-family="${FONT}">${count}</text>` : ''}
        <circle cx="${cx + 14}" cy="${cy - 14}" r="5.5" fill="${freshColor}" stroke="#ffffff" stroke-width="2"/>
    </svg>`;
    return icon(svg, width, height, cx, cy, 'lm-ico-courier');
}

/** Teslim noktası: numaralı damla (kartlardaki sırayla eşleşir) + fiş balonu. */
export function dropoffIcon({ color, num, label, atRestaurant }) {
    const PIN_W = 30;
    const PIN_H = 40;
    const text = atRestaurant ? `${label}, restoranda` : label;
    const bw = bubbleWidth(text, 46, 18);
    const block = BUBBLE_H + 3;
    const width = Math.max(PIN_W, bw);
    const height = block + PIN_H;
    const drop = atRestaurant
        ? `<path d="M15 1.5C7.5 1.5 1.5 7.5 1.5 15c0 9.6 13.5 22.8 13.5 22.8S28.5 24.6 28.5 15C28.5 7.5 22.5 1.5 15 1.5z" fill="#ffffff" stroke="${color}" stroke-width="3"/>
           <text x="15" y="19.5" text-anchor="middle" font-size="12" font-weight="900" fill="${color}" font-family="sans-serif">${num}</text>`
        : `<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#fff" stroke-width="2"/>
           <circle cx="15" cy="15" r="8" fill="#fff"/>
           <text x="15" y="19" text-anchor="middle" font-size="11" font-weight="800" fill="${color}" font-family="sans-serif">${num}</text>`;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${SHADOW}
        <rect x="${(width - bw) / 2 + 1}" y="1" width="${bw - 2}" height="${BUBBLE_H - 2}" rx="11" fill="#ffffff" stroke="${color}" stroke-width="2"${atRestaurant ? ' stroke-dasharray="4 3"' : ''} filter="url(#s)"/>
        <text x="${width / 2}" y="16.5" text-anchor="middle" font-size="12" font-weight="800" fill="#0f172a" font-family="${FONT}">${esc(text)}</text>
        <g transform="translate(${(width - PIN_W) / 2},${block})">${drop}</g>
    </svg>`;
    return icon(svg, width, height, width / 2, height, 'lm-ico-drop');
}

/** Atanmamış paket (teslimat adresinde): kare + ünlem; şekliyle ve rengiyle diğerlerinden ayrışır. */
export function unassignedIcon(label) {
    const PIN_W = 36;
    const PIN_H = 44;
    const bw = label ? bubbleWidth(label, 48, 20) : 0;
    const block = label ? BUBBLE_H + 3 : 0;
    const width = Math.max(PIN_W, bw);
    const height = block + PIN_H;
    const bubble = label ? `
        <rect x="${(width - bw) / 2 + 1}" y="1" width="${bw - 2}" height="${BUBBLE_H - 2}" rx="11" fill="${UNASSIGNED_DARK}" stroke="#fff" stroke-width="1.5" filter="url(#s)"/>
        <text x="${width / 2}" y="16.5" text-anchor="middle" font-size="12" font-weight="800" fill="#fff" font-family="${FONT}">${esc(label)}</text>` : '';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${SHADOW}
        ${bubble}
        <g transform="translate(${(width - PIN_W) / 2},${block})">
            <rect x="0.5" y="0.5" width="35" height="35" rx="10" fill="${UNASSIGNED_COLOR}" opacity="0.28"/>
            <path d="M18 44l-7-9h14z" fill="${UNASSIGNED_COLOR}"/>
            <rect x="4" y="4" width="28" height="28" rx="8" fill="${UNASSIGNED_COLOR}" stroke="#fff" stroke-width="2.5"/>
            <text x="18" y="24.5" text-anchor="middle" font-size="18" font-weight="900" fill="#fff" font-family="sans-serif">!</text>
        </g>
    </svg>`;
    return icon(svg, width, height, width / 2, height, 'lm-ico-unassigned');
}

/** Paketin alınacağı restoran: koyu damla + ev. */
export function restaurantIcon() {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 40 50">
        <path d="M20 0C9 0 0 9 0 20c0 14 20 30 20 30s20-16 20-30C40 9 31 0 20 0z" fill="#1e293b" stroke="#fff" stroke-width="2.5"/>
        <path d="M20 9l-10 8.5V29h7v-7h6v7h7V17.5z" fill="#fff"/>
    </svg>`;
    return icon(svg, 34, 42, 17, 42, 'lm-ico-restaurant');
}

/** Rotanın ortasındaki etiket çipi: mesafe, süre, varış. Metin DOM ile yazılır. */
export function routeLabelIcon(text, color) {
    const el = document.createElement('span');
    el.className = 'lm-route-label';
    el.style.setProperty('--route', color);
    el.textContent = text;
    return L.divIcon({ html: el, className: 'lm-ico lm-ico-label', iconSize: null, iconAnchor: [0, 12] });
}
